/**
 * Derives a Google-native embeddable preview URL from a Drive file's id and
 * mime type. All returned URLs are `/preview` endpoints that render inside an
 * <iframe> without requiring the same-origin X-Frame-Options exemption that
 * the /edit URLs carry. Authentication is handled by the user's browser Google
 * session; if unauthenticated, Google shows its own sign-in UI inside the frame.
 *
 * Returns null for entity types that have no embeddable preview (folders,
 * forms, shortcuts, drive-sdk apps). Callers should fall back to an external
 * link button when null is returned.
 */
export function getGoogleDrivePreviewUrl(
  id: string,
  mimeType: string | undefined | null,
  platformName?: string | null,
): string | null {
  if (!id) return null;

  // Generic entities (opened from a generic card) carry no mime_type. A Doc or
  // Sheet's id IS its Drive file id, so derive the Workspace viewer from the
  // platform; the universal previewer below handles plain Drive files.
  if (!mimeType && platformName) {
    const p = platformName.trim().toLowerCase();
    if (p === "google-docs" || p === "google_docs") {
      return `https://docs.google.com/document/d/${id}/preview`;
    }
    if (p === "google-sheets" || p === "google_sheets") {
      return `https://docs.google.com/spreadsheets/d/${id}/preview`;
    }
  }

  const mime = mimeType ?? "";

  // Google Workspace native docs — each has a dedicated apps viewer
  if (mime === "application/vnd.google-apps.document") {
    return `https://docs.google.com/document/d/${id}/preview`;
  }
  if (mime === "application/vnd.google-apps.spreadsheet") {
    return `https://docs.google.com/spreadsheets/d/${id}/preview`;
  }
  if (mime === "application/vnd.google-apps.presentation") {
    return `https://docs.google.com/presentation/d/${id}/preview`;
  }
  if (mime === "application/vnd.google-apps.drawing") {
    return `https://docs.google.com/drawings/d/${id}/preview`;
  }
  if (mime === "application/vnd.google-apps.form") {
    // Forms have an embeddable viewform URL but not a typical /preview
    return `https://docs.google.com/forms/d/${id}/viewform?embedded=true`;
  }

  // Non-previewable Google types — open externally
  if (
    mime === "application/vnd.google-apps.folder" ||
    mime === "application/vnd.google-apps.shortcut" ||
    mime.startsWith("application/vnd.google-apps.drive-sdk")
  ) {
    return null;
  }

  // Everything else: Office files, PDFs, images, video, audio, text, unknown
  // → Drive's universal file previewer handles all of these natively
  return `https://drive.google.com/file/d/${id}/preview`;
}

/**
 * Drive's `icon_link` points at a 16px asset
 * (`drive-thirdparty.googleusercontent.com/16/type/...`). Google serves the
 * identical icons at 32/64/128px — swap the size path segment so icons
 * rendered larger than 16px stay crisp.
 */
export function upscaleDriveIcon(
  url: string | undefined | null,
  size = 64,
): string | undefined {
  if (!url) return undefined;
  return url.replace(
    /(drive-thirdparty\.googleusercontent\.com)\/\d+\//,
    `$1/${size}/`,
  );
}
